import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomChip from '@ui-components/Chip/CustomChip';

import FilterIcon from '@assets/images/Filter.svg';
import SearchIcon from '@assets/images/search.svg';
import CustomButton from '@ui-components/Button/CustomButton';
import { rowsPerPage } from '../constants';

import { useLocation, useNavigate } from 'react-router-dom';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import useDebouncing from '../components/useDebouncing';
import { getEndItem, getSortIcon, getStartItem } from '../components/Sorting';
import {
  useGetAzureEntraUsers,
  useGetClinicianList,
} from '../../API/hooks/ClinicianHook';
import FetchEntraUsers from '../components/FetchEntraUsers';
import {
  CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH,
  CLINICIANS_ROUTE_PATH,
  NEW_CLINICIANS_ROUTE_PATH,
  VIEW_CLINICIANS_ROUTE_PATH,
} from '../../routers/routePaths';
import ClinicianTable from './clinicianTable';
import { useGetPartnerFacilityFilterData } from '../../API/hooks/PartnerFacilityFilterHook';
import CustomPopover from '@ui-components/Popover/CustomPopover';
import { StatusFilterSelector } from '../components/StatusFilterSelector';
import { CustomBadge, CustomDivider } from '@qler-view/shared_lib/src';
import { hasRole } from '../login/storageUtility';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import { useFilters } from '../../routers/FilterContext';

const CliniciansList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openAzureEntraUsers, setOpenAzureEntraUsers] = useState(false);

  const [page, setPage] = useState(0);

  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortType, setSortType] = useState<'asc' | 'desc' | undefined>(
    undefined
  );
  const query = new URLSearchParams(location.search);
  const toast = query.get('toast');
  const { filterContext, setFilterContext, getFilterCounts } = useFilters();
  const {
    azureEntraUsers,
    refetch: refetchAzureEntraUsers,
    isLoading: isLoadingAzureEntraUsers,
  } = useGetAzureEntraUsers('CLINICIAN');

  const [searchTermCliniciansList, setSearchTermCliniciansList] =
    useState<string>(filterContext.searchedValue);

  const debouncedSearchTerm = useDebouncing(searchTermCliniciansList, 500);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus(); // Focus the input field
    }
  }, [searchTermCliniciansList]);
  const [anchorFacilityListEl, setanchorFacilityListEl] =
    useState<HTMLElement | null>(null);
  const { PartnerFacilityFilterData, refetch: refetchFilters } =
    useGetPartnerFacilityFilterData();
  const handleFacilityListFilterClick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setanchorFacilityListEl(event.currentTarget);
    refetchFilters();
  };
  const [selectedStatusId, setSelectedStatusId] = useState<number[]>(
    filterContext.statusIds
  );
  const [filterStatusId, setFilterStatusId] = useState<number[]>(
    filterContext.statusIds
  );
  const handleOpenAzureEntraForm = (open: boolean) => {
    setOpenAzureEntraUsers(open);
    refetchAzureEntraUsers();
  };
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, selectedStatusId]);

  useEffect(() => {
    setSelectedStatusId(filterContext.statusIds);
    setFilterStatusId(filterContext.statusIds);
    setSearchTermCliniciansList(filterContext.searchedValue);
  }, [filterContext]);

  const { ClinicianListResponse, isLoading } = useGetClinicianList({
    userId: '1',
    page,
    size: rowsPerPage,
    sortField,
    sortType,
    searchTerm: debouncedSearchTerm,
    statusId: selectedStatusId,
  });

  const ClinicianListCount = ClinicianListResponse?.totalRecords ?? 0;
  const handleStatusFilter = (statusIds: number[]) => {
    setFilterStatusId(statusIds);
  };
  const handleApplyFilters = () => {
    setSelectedStatusId(filterStatusId);
    setFilterContext({
      ...filterContext,
      statusIds: filterStatusId, // update only this
    });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSort = (field: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortField === field && sortType === 'asc') {
      direction = 'desc';
    }
    setSortField(field);
    setSortType(direction);
  };

  const handleCliniciansListSearch = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value;

    // Remove leading spaces
    value = value.replace(/^\s+/, '');

    // Replace multiple spaces with a single space
    value = value.replaceAll(/\s{2,}/g, ' ');

    setSearchTermCliniciansList(value);
    setFilterContext({
      ...filterContext,
      searchedValue: value,
    });
  };

  const handleEditClinicianInfo = (clinicianId: number) => {
    navigate(NEW_CLINICIANS_ROUTE_PATH + `/${clinicianId}`, {
      replace: true,
      state: {
        isClinicianEdit: true,
      },
    });
  };
  const handleViewClinicianInfo = (clinicianId: number) => {
    navigate(VIEW_CLINICIANS_ROUTE_PATH + `/${clinicianId}`, { replace: true });
  };

  const handleClinicianFacilityMapList = (
    clinicianId: number,
    providerName: string | null
  ) => {
    navigate(CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH + `/${clinicianId}`, {
      state: {
        providerName: providerName,
        type: 'clinician',
        ids: clinicianId,
      },
      replace: true,
    });
  };

  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (toast === 'success') {
      setMessage('Clinician saved successfully!');
      setOpenToast(true);
    } else if (toast === 'update') {
      setMessage('Clinician updated successfully!');
      setOpenToast(true);
    }
    navigate(CLINICIANS_ROUTE_PATH, { replace: true });
  }, [toast, navigate]);

  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };

  return (
    <Box>
      {openToast && (
        <CustomSnackbar
          id="clinicians-list-snackbar"
          successOrError={'success'}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={message}
        />
      )}
      <Box className="pt-20">
        <Grid2 container className="align-items-center">
          <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
            <CustomTypography
              id="page-title-clinicians-list"
              variant="h4"
              className="text-align-left"
            >
              Clinicians
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
            <Box className="flex float-right">
              <Box>
                <CustomTextField
                  id="txt-clinicians-list"
                  name="search-clinicians-list"
                  placeholder="Search Clinicians"
                  size="small"
                  type="text"
                  value={searchTermCliniciansList}
                  onChange={handleCliniciansListSearch}
                  startAdornment={
                    <CustomIconButton
                      id="search-icon"
                      size="small"
                      icon={<SearchIcon />}
                    />
                  }
                />
              </Box>
              <Box className="ml-20">
                <CustomBadge
                  id={`notification`}
                  badgeContent={getFilterCounts(filterContext).totalCount}
                  color="error"
                  max={99}
                >
                  <CustomChip
                    aria-haspopup="true"
                    onClick={(e) => {
                      handleFacilityListFilterClick(e);
                    }}
                    className="box-border2"
                    id="demo-chip"
                    label="Filters"
                    variant="outlined"
                    icon={<FilterIcon />}
                    iconPosition="left"
                  />
                </CustomBadge>
              </Box>
              {hasRole(['ops-admin']) && (
                <Box className="ml-20">
                  <CustomButton
                    variant="contained"
                    id="btn-add-clinician"
                    color="primary"
                    size="large"
                    onClick={() => {
                      handleOpenAzureEntraForm(true);
                    }}
                  >
                    Add Clinician
                  </CustomButton>
                </Box>
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>

      <Box className="pt-20">
        <ClinicianTable
          isLoading={isLoading}
          clinicians={ClinicianListResponse?.cliniciansList ?? []}
          totalRecords={ClinicianListCount}
          page={page}
          rowsPerPage={rowsPerPage}
          sortField={sortField ?? ''}
          sortType={sortType ?? 'desc'}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onEdit={handleEditClinicianInfo}
          onView={handleViewClinicianInfo}
          onFacilityCount={handleClinicianFacilityMapList}
          getSortIcon={getSortIcon}
          getStartItem={getStartItem}
          getEndItem={getEndItem}
          action="list"
        />
      </Box>
      <Box>
        <Box>
          <FetchEntraUsers
            openAzureEntraUsers={openAzureEntraUsers}
            onAzureEntraClose={() => handleOpenAzureEntraForm(false)}
            azureEntraUsers={azureEntraUsers ?? []}
            isLoadingAzureEntraUsers={isLoadingAzureEntraUsers}
            path={NEW_CLINICIANS_ROUTE_PATH}
            title="Clinician"
          />
        </Box>
      </Box>
      <Box>
        <CustomPopover
          open={Boolean(anchorFacilityListEl)}
          anchorEl={anchorFacilityListEl}
          onClose={() => setanchorFacilityListEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          className="mt-4"
        >
          <Box className="p-24">
            <Box className="text-align-left">
              <Box>
                <CustomTypography
                  id="intake-form-filter-title"
                  variant="h4"
                  color="textPrimary"
                >
                  Filters
                </CustomTypography>
                <Box className="pt-8">
                  <CustomTypography
                    id="intake-form-filter-subtitle"
                    variant="body2"
                    color="textDisabled"
                  >
                    Filter records by status
                  </CustomTypography>
                </Box>
              </Box>
            </Box>
            <Box className="pt-16">
              <CustomDivider />
            </Box>
            <Box className="flex flex-column pt-16">
              <Grid2 container justifyContent="center" spacing={2}>
                <Grid2 size={{ xs: 12 }}>
                  <Box>
                    <Box className="flex-between-center">
                      <Box>
                        <CustomTypography
                          id="facility-list-status-filter-title"
                          variant="body2"
                          color="textDisabled"
                        >
                          STATUS
                        </CustomTypography>
                      </Box>
                    </Box>
                    <Box className="pt-8">
                      <StatusFilterSelector
                        data={PartnerFacilityFilterData}
                        value={filterStatusId}
                        isForConsult={false}
                        onChange={handleStatusFilter}
                      />
                    </Box>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
            <Box className="pt-16">
              <Grid2 container>
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <Box className="display-flex justify-end">
                    <Box className="pr-12">
                      <CustomButton
                        id="cancel-facility-user-button"
                        onClick={() => {
                          setanchorFacilityListEl(null);
                          setFilterStatusId([]);
                          setSelectedStatusId([]);
                          setFilterContext({
                            ...filterContext,
                            facilities: {},
                            statusIds: [],
                          });
                        }}
                        variant="outlined"
                      >
                        Clear
                      </CustomButton>
                    </Box>
                    <Box>
                      <CustomButton
                        id="apply-facility-user-filter-button"
                        onClick={() => {
                          setanchorFacilityListEl(null);
                          handleApplyFilters();
                        }}
                        variant="contained"
                      >
                        Apply
                      </CustomButton>
                    </Box>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
          </Box>
        </CustomPopover>
      </Box>
    </Box>
  );
};

export default CliniciansList;
